import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { adminResetPassword } from '../../features/users/users'
import { selectFeatureFlag } from '../../features/featureFlags/selectors'
import { ADMIN_RESET_SHOWS_PASSWORD } from '../../config/featureFlagKeys'
import { showSnackBar } from '../../components/Snackbar/Snackbar'
import { ResetPasswordIcon, SuccessCheckIcon } from '../../components/Icons/Icons'

const AdminResetPassword = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const userId = searchParams.get('userId')
  const userType = searchParams.get('userType')
  const userEmail = searchParams.get('email')
  const showsPassword = useSelector(selectFeatureFlag(ADMIN_RESET_SHOWS_PASSWORD))

  const [step, setStep] = useState('confirm')
  const [newPassword, setNewPassword] = useState('')
  const [resolvedEmail, setResolvedEmail] = useState('')
  const [redirected, setRedirected] = useState(false)
  const [linkKind, setLinkKind] = useState('')
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [pendingMode, setPendingMode] = useState('')

  const handleReset = async (mode) => {
    setLoading(true)
    setPendingMode(mode || '')
    try {
      const result = await dispatch(adminResetPassword({ userId, userType, mode }))
      if (result?.payload?.data?.linkSent) {
        const data = result.payload.data
        setResolvedEmail(data.email || userEmail)
        setRedirected(!!data.redirected)
        setLinkKind(data.linkKind)
        setStep('linkSent')
      } else if (result?.payload?.data?.newPassword) {
        const data = result.payload.data
        setNewPassword(data.newPassword)
        setResolvedEmail(data.email || userEmail)
        setRedirected(!!data.redirected)
        setStep('result')
      } else {
        const msg = typeof result.payload === 'string' ? result.payload : 'Failed to reset password'
        showSnackBar('error', msg)
      }
    } catch (e) {
      showSnackBar('error', 'Failed to reset password')
    } finally {
      setLoading(false)
      setPendingMode('')
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newPassword)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      showSnackBar('error', 'Failed to copy')
    }
  }

  return (
    <>
      <style>{`
        .arp-root {
          min-height: 100vh;
          background: #0f0f0f;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'DM Sans', sans-serif;
        }
        .arp-card {
          background: #161616;
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 16px;
          padding: 56px;
          width: 100%;
          max-width: 480px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        }
        .arp-heading {
          color: #ffffff;
          font-size: 28px;
          font-weight: 600;
          margin: 0;
          text-align: center;
        }
        .arp-subtitle {
          color: rgba(255,255,255,0.45);
          font-size: 16px;
          line-height: 1.6;
          text-align: center;
          margin: 0;
        }
        .arp-email {
          color: #ffffff;
          font-weight: 600;
          font-size: 17px;
        }
        .arp-btn {
          width: 100%;
          background: #6b63f5;
          color: #ffffff;
          border: none;
          border-radius: 10px;
          padding: 16px;
          font-size: 16px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s;
        }
        .arp-btn:hover {
          background: #7c75f7;
        }
        .arp-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .arp-btn--secondary {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.15);
        }
        .arp-btn--secondary:hover {
          background: rgba(255,255,255,0.05);
        }
        .arp-btn--success {
          background: #22c55e;
        }
        .arp-btn--success:hover {
          background: #16a34a;
        }
        .arp-password-box {
          width: 100%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          padding: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        }
        .arp-password-text {
          color: #ffffff;
          font-size: 22px;
          font-family: 'DM Mono', 'DM Sans', monospace;
          letter-spacing: 0.08em;
          user-select: all;
        }
        .arp-link {
          color: rgba(255,255,255,0.45);
          font-size: 15px;
          text-decoration: none;
          cursor: pointer;
          transition: color 0.2s;
          background: none;
          border: none;
          font-family: 'DM Sans', sans-serif;
        }
        .arp-link:hover {
          color: rgba(255,255,255,0.7);
        }
        .arp-actions {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
      `}</style>
      <div className="arp-root">
        <div className="arp-card">
          {step === 'confirm' && (
            <>
              <ResetPasswordIcon />
              <h2 className="arp-heading">Reset Password</h2>
              <p className="arp-subtitle">
                Are you sure you want to reset the password for<br />
                <span className="arp-email">{userEmail}</span>?
              </p>
              <div className="arp-actions">
                {showsPassword ? (
                  <>
                    <button className="arp-btn" onClick={() => handleReset('password')} disabled={loading}>
                      {pendingMode === 'password' ? 'Generating...' : 'Generate password'}
                    </button>
                    <button className="arp-btn" onClick={() => handleReset('link')} disabled={loading}>
                      {pendingMode === 'link' ? 'Sending...' : 'Email reset link'}
                    </button>
                  </>
                ) : (
                  <button className="arp-btn" onClick={() => handleReset()} disabled={loading}>
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                )}
                <button className="arp-btn arp-btn--secondary" onClick={() => navigate(-1)}>
                  Cancel
                </button>
              </div>
            </>
          )}
          {step === 'result' && (
            <>
              <SuccessCheckIcon />
              <h2 className="arp-heading">Password Reset Successfully</h2>
              <p className="arp-subtitle">
                New password for <span className="arp-email">{resolvedEmail || userEmail}</span>
              </p>
              {redirected && resolvedEmail && (
                <p className="arp-subtitle" role="alert" style={{ color: '#f5b544' }}>
                  Applied to the primary record ({resolvedEmail}), not the duplicate you opened.
                </p>
              )}
              <div className="arp-password-box">
                <span className="arp-password-text">{newPassword}</span>
              </div>
              {showsPassword && (
                <p className="arp-subtitle">Copy it now; it will not be shown again.</p>
              )}
              <div className="arp-actions">
                <button
                  className={`arp-btn ${copied ? 'arp-btn--success' : ''}`}
                  onClick={handleCopy}
                >
                  {copied ? 'Copied!' : 'Copy Password'}
                </button>
                <button className="arp-link" onClick={() => navigate(-1)}>
                  ← Back to previous page
                </button>
              </div>
            </>
          )}
          {step === 'linkSent' && (
            <>
              <SuccessCheckIcon />
              <h2 className="arp-heading">Link sent</h2>
              {linkKind === 'setup' ? (
                <p className="arp-subtitle">
                  We emailed <span className="arp-email">{resolvedEmail || userEmail}</span> a link to create their password.
                </p>
              ) : (
                <p className="arp-subtitle">
                  We emailed a reset link to <span className="arp-email">{resolvedEmail || userEmail}</span>. Their current
                  password keeps working until they use it.
                </p>
              )}
              {redirected && resolvedEmail && (
                <p className="arp-subtitle" role="alert" style={{ color: '#f5b544' }}>
                  Applied to the primary record ({resolvedEmail}), not the duplicate you opened.
                </p>
              )}
              <div className="arp-actions">
                <button className="arp-link" onClick={() => navigate(-1)}>
                  ← Back to previous page
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default AdminResetPassword
